import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Button, ButtonIcon, H3 } from '../../components';
import { lineHeight, theme, toRem } from '../../design';

export const EnabledWrapper = styled.div`
  margin: ${toRem(theme.space.lg)} 0;

  > p {
    color: ${theme.color.neutral[600]};
  }
`;

export const DisabledWrapper = styled.div`
  flex-direction: column;
  gap: ${toRem(theme.space.sm)};
  margin-top: ${toRem(theme.space.lg)};

  > p {
    &:last-child {
      color: ${theme.color.neutral[600]};
    }
  }
`;

export const LoadingSkeleton = styled(Skeleton)`
  && {
    width: 100%;
    border: ${toRem(1)} solid ${theme.color.neutral[200]};
    border-radius: ${toRem(8)};
    height: ${toRem(61)};
    margin-top: ${toRem(theme.space.md)};
  }
`;

export const TipCalculationsContainer = styled.div<{ disabled?: boolean; $noMargin?: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  padding: ${toRem(theme.space.xl)};
  gap: ${toRem(theme.space.lg)};
  background: ${({ disabled }) => (!disabled ? theme.color.neutral[100] : theme.color.neutral[25])};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  align-self: stretch;
  flex-grow: 0;
  margin-top: ${({ $noMargin }) => !$noMargin && toRem(theme.space.lg)};
`;

export const SettingCardContainer = styled(TipCalculationsContainer)`
  ${({ disabled }) =>
    disabled &&
    css`
      padding: ${toRem(theme.space.lg)};
      background: ${theme.color.white};
    `}

  form {
    align-items: flex-start;
    gap: ${toRem(theme.space['2xs'])};

    label {
      font-weight: ${theme.font.weight.regular};

      .optional {
        color: ${theme.color.neutral[400]};
      }
    }
  }
`;

export const Title = styled(H3)`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

export const TitleRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const AddButton = styled(Button)<{ $tipShare?: boolean }>`
  width: fit-content !important;
  padding: 0 ${toRem(theme.space.lg)} !important;
  height: ${toRem(34)};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};

  svg {
    height: ${toRem(20)};
    width: ${toRem(20)};
    fill: ${theme.color.neutral[700]};
  }

  ${({ $tipShare }) =>
    $tipShare &&
    css`
      height: ${toRem(32)};
      padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)} !important;
      color: ${theme.color.neutral[500]};
      background-color: ${theme.color.neutral[50]};
      border: 1px solid ${theme.color.neutral[500]};

      svg {
        fill: ${theme.color.neutral[500]} !important;
      }

      &:hover,
      &:focus-visible {
        color: ${theme.color.neutral[700]};
        background-color: ${theme.color.neutral[50]};

        svg {
          fill: ${theme.color.neutral[700]} !important;
        }
      }
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      svg {
        fill: ${theme.color.neutral[400]};
      }
    `}
`;

export const ActionSection = styled.section<{ $marginTop?: number }>`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  position: relative;
  margin-top: ${({ $marginTop }) => toRem($marginTop ?? theme.space.lg)};
  width: 100%;

  button {
    width: 48% !important;
    height: ${toRem(34)};
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.semiBold};

    @media (max-width: ${theme.breakpoint.small}px) {
      width: 100% !important;
      margin: ${toRem(theme.space.sm)} 0;
    }

    &:disabled {
      color: ${theme.color.neutral[400]};
      background-color: ${theme.color.neutral[200]};

      &:hover {
        color: ${theme.color.neutral[400]};
        background-color: ${theme.color.neutral[200]};
      }
    }
  }
`;

export const StyledIconButton = styled(ButtonIcon)<{ $keepStyles?: boolean; $noMargin?: boolean; $hideMobile?: boolean }>`
  width: ${toRem(24)} !important;
  height: ${toRem(24)} !important;
  margin-left: ${(props) => !props.$noMargin && toRem(theme.space.sm)};

  svg {
    fill: ${(props) => (props.$keepStyles ? `${theme.color.neutral[500]} !important` : theme.color.neutral[500])};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.color.neutral[100]} !important;
    `}

  ${({ $hideMobile }) =>
    $hideMobile &&
    css`
      @media (max-width: ${theme.breakpoint.med}px) {
        display: none;
      }
    `}
`;

export const FieldsContainer = styled.div`
  flex-direction: column;
  gap: ${toRem(theme.space.md)};
  width: 100%;
`;

export const StyledItem = styled.div`
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.lg)};
  background: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};
  min-height: ${toRem(61)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    gap: 0;
  }
`;

export const ItemContainer = styled.div<{ noGap?: boolean }>`
  flex-direction: column;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
  background: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(8)};

  ${({ noGap }) =>
    !noGap &&
    css`
      gap: ${toRem(theme.space.sm)};
    `}
`;

export const DetailsContainer = styled.ul`
  flex-direction: column;
  padding: ${toRem(theme.space.lg)};
  border-radius: ${toRem(theme.borderRadius.small)};
  background-color: ${theme.color.neutral[50]};
`;

export const DetailsItem = styled.li`
  margin-left: ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.xxs)};
  color: ${theme.color.neutral[500]};
`;

export const StyledItemTextContainer = styled.div`
  flex-direction: column;
  min-width: 0;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:last-child {
      color: ${theme.color.neutral[500]};
      white-space: normal;
    }
  }
`;

export const ReadOnlyContainer = styled.div<{ $tipShare?: boolean }>`
  background-color: ${({ $tipShare }) => ($tipShare ? theme.color.neutral[50] : theme.color.neutral[100])};
  padding: ${toRem(theme.space.md)};
  height: 100%;
  width: 100%;
  align-items: center;
  flex-grow: 1;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(5.5)};
  justify-content: space-between;
`;

export const StyledLink = styled.a`
  color: ${theme.color.primary[600]};
`;
